import clsx from 'clsx';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Coffee, Hourglass, LogIn, LogOut, MonitorOff, Pause, Play, ShieldCheck, Sparkles, Undo2, UserX } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatDays, formatMinutes, formatShortTime, isoDate, relativeTime } from '../lib/format';
import {
  actionsOf,
  agentBlockKey,
  formatClock,
  isFreeShift,
  isPause,
  overrunCharged,
  overrunPenalised,
  presenceLabel,
  ShiftConfirm,
  useAttendance,
  useElapsed,
  usedUpToday,
  waitingRequest,
  type AttendanceSession,
  type BreakRequestRef,
  type DayHours,
  type DaySummary,
  type FreeShifts,
  type PresenceView,
  type ShiftAction,
} from '../components/ShiftControl';
import { AgentPanel } from '../components/AgentPanel';
import { BREAK_REQUEST_TONE, BreakRequestsBoard } from './BreakRequests';
import { DailyReport } from './AttendanceReport';
import { useBreakApprover, useWaitingBreakRequests } from '../lib/break-requests';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Select,
  StatusDot,
  Table,
  Tabs,
  Td,
  Th,
  TimeRange,
  Tr,
  useToast,
  type BadgeTone,
} from '../components/ui';

/**
 * Route: `/attendance` — my shift for everyone; the team board, the daily report
 * (`?tab=report&date=…`) and their computers for attendance.manage; the break
 * requests waiting for approval for whoever
 * decides them — attendance.breaks.approve, or picked on a break type
 * (`?tab=requests&request=<id>` brings one into view).
 */
export function AttendancePage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const manager = can(P.ATTENDANCE_MANAGE);
  const approver = useBreakApprover();
  const waiting = useWaitingBreakRequests();
  const requested = params.get('tab');
  const tab =
    (manager && (requested === 'team' || requested === 'report' || requested === 'computers')) || (approver && requested === 'requests')
      ? requested!
      : 'mine';

  return (
    <>
      <PageHeader title={t('attendance.title')} />
      {(manager || approver) && (
        <div className="mb-5">
          <Tabs
            active={tab}
            onChange={(id) => setParams(id === 'mine' ? {} : { tab: id }, { replace: true })}
            tabs={[
              { id: 'mine', label: t('attendance.myShift') },
              ...(manager
                ? [
                    { id: 'team', label: t('attendance.team') },
                    { id: 'report', label: t('attendance.dailyReport') },
                    { id: 'computers', label: t('attendance.computers') },
                  ]
                : []),
              ...(approver ? [{ id: 'requests', label: t('attendance.breakRequests'), count: waiting || undefined }] : []),
            ]}
          />
        </div>
      )}
      {tab === 'team' ? (
        <TeamBoard />
      ) : tab === 'report' ? (
        <DailyReport />
      ) : tab === 'computers' ? (
        <ComputersBoard />
      ) : tab === 'requests' ? (
        <BreakRequestsBoard focus={params.get('request')} />
      ) : (
        <MyShift />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Presence                                                                    */
/* -------------------------------------------------------------------------- */

/** "Not working — Idle" as a badge: never a break, never the end of the shift. */
export function PresenceBadge({ presence, compact = false }: { presence: PresenceView | null | undefined; compact?: boolean }) {
  const { t } = useI18n();
  if (!presence?.tracked || presence.status !== 'NOT_WORKING') return null;
  return (
    <Badge tone="error" dot className={compact ? 'mt-0.5' : undefined}>
      {presenceLabel(presence, t)}
    </Badge>
  );
}

/* -------------------------------------------------------------------------- */
/* My shift                                                                    */
/* -------------------------------------------------------------------------- */

function MyShift() {
  const { t, locale, localized } = useI18n();
  const [params] = useSearchParams();
  const attendance = useAttendance();
  const [asking, setAsking] = useState<ShiftAction | null>(null);
  const data = attendance.data;
  const sessionSeconds = useElapsed(data?.session?.startedAt, data?.now);
  const breakSeconds = useElapsed(data?.openBreak?.startedAt, data?.now);

  const history = useQuery({
    queryKey: ['attendance', 'history'],
    queryFn: () => api.get<ShiftHistory>('/attendance/history'),
  });

  if (attendance.isLoading) return <LoadingBlock />;
  if (attendance.error || !data) return <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />;

  const working = data.state === 'WORKING';
  const onBreak = data.state === 'ON_BREAK';
  // Pause work (2026-10-24): the open break is the pause.
  const paused = data.state === 'PAUSED';
  // A Free Shift (CR-006): tracked, unpaid, no breaks.
  const free = data.state === 'FREE_SHIFT';
  const notWorking = (working || free) && data.presence?.status === 'NOT_WORKING';
  const breakType = data.openBreak?.breakType ?? null;
  const breakOver = onBreak && breakType?.maxMinutes != null && breakSeconds > breakType.maxMinutes * 60;
  const summary = data.summary;
  // What may start now is the server's: the day's Regular Shift once, then Free Shifts.
  const actions = actionsOf(data);
  const startable = data.state === 'OFF_DUTY' || data.stale;
  const regularOver = data.state === 'OFF_DUTY' && data.regular?.status === 'COMPLETED';
  const blocked = startable && (actions.canStartRegular || actions.canStartFree) && !data.agent.canStart;
  // A break asked for, waiting for an administrator, and what came of this shift's earlier ones.
  const waiting = waitingRequest(data);
  const decided = (data.breakRequests ?? []).filter((r) => r.status !== 'PENDING');
  // Whoever may pause: what they paused today, and the day's hours they still owe.
  const pauses = !!data.pause?.allowed || (summary.pausedMinutes ?? 0) > 0;

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-overline uppercase text-[var(--text-tertiary)]">
              <StatusDot
                tone={onBreak || paused ? 'idle' : notWorking ? 'alert' : working || free ? 'online' : 'offline'}
                pulse={(working || free) && !notWorking}
              />
              {onBreak
                ? t('attendance.onBreak')
                : paused
                  ? t('attendance.paused')
                  : notWorking
                    ? t('presence.NOT_WORKING')
                    : free
                      ? t('attendance.freeShift')
                      : working
                        ? t('attendance.working')
                        : t('attendance.offDuty')}
            </p>
            <p className={clsx('mt-2 text-metric tabular', breakOver ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]')} dir="ltr">
              {onBreak || paused ? formatClock(breakSeconds) : working || free ? formatClock(sessionSeconds) : '—'}
            </p>
            <p className="mt-1 text-body-sm text-muted">
              {free && data.session
                ? t('attendance.freeSince', { time: formatShortTime(data.session.startedAt, locale) })
                : paused
                ? t('attendance.pausedSince', { time: formatShortTime(data.openBreak!.startedAt, locale) })
                : onBreak && breakType
                  ? `${localized(breakType)} · ${t('attendance.since', { time: formatShortTime(data.openBreak!.startedAt, locale) })}${
                      breakType.maxMinutes ? ` · ${breakType.maxMinutes} ${t('common.minutesShort')}` : ''
                    }`
                  : data.session
                    ? t(notWorking ? 'attendance.shiftSince' : 'attendance.since', { time: formatShortTime(data.session.startedAt, locale) })
                    : data.roster?.startAt
                      ? t('attendance.promptRoster', {
                          shift: localized({ name: data.roster.shiftName ?? '', nameAr: data.roster.shiftNameAr }),
                          start: formatShortTime(data.roster.startAt, locale),
                          end: formatShortTime(data.roster.endAt, locale),
                        })
                      : t('attendance.noRoster')}
            </p>
            {free && <p className="mt-2 max-w-xl text-body-sm text-muted">{t('attendance.freeUnpaid')}</p>}
            {(working || free) && data.presence?.tracked && (
              <div className="mt-3 max-w-xl space-y-1">
                {notWorking ? (
                  <>
                    <p className="text-body-sm font-medium text-error-fg dark:text-error-dark">
                      {presenceLabel(data.presence, t)} · {t('attendance.since', { time: formatShortTime(data.presence.since, locale) })}
                    </p>
                    {data.presence.penaltyDueAt && (
                      <p className="text-body-sm text-error-fg dark:text-error-dark">
                        {t('presence.penaltyAt', {
                          days: formatDays(data.agent.penaltyDays, locale),
                          time: formatShortTime(data.presence.penaltyDueAt, locale),
                        })}
                      </p>
                    )}
                    <p className="text-body-sm text-muted">
                      {data.presence.reason === 'AGENT_DISCONNECTED' || data.presence.reason === 'DEVICE_SLEEPING'
                        ? t('presence.reconnectHint', { name: data.presence.device?.name ?? '' })
                        : t('presence.backHint')}
                    </p>
                  </>
                ) : (
                  <p className="text-body-sm text-muted">{t('presence.trackedOn', { name: data.presence.device?.name ?? '' })}</p>
                )}
              </div>
            )}
            {(working || free) && data.presence && !data.presence.tracked && (
              <p className="mt-2 text-body-sm text-muted">{t('presence.untracked')}</p>
            )}
            {/* Its Regular Shift over (CR-006): never offered again today; when the next opens, and Free Shifts meanwhile. */}
            {regularOver && (
              <div className="mt-3 flex max-w-xl gap-2.5 rounded-md border hairline bg-[var(--surface-sunken)] px-3.5 py-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-fg dark:text-success" aria-hidden />
                <div className="min-w-0">
                  <p className="text-body-sm font-medium text-[var(--text-primary)]">{t('attendance.regularDone')}</p>
                  <p className="text-body-sm text-muted">
                    {data.nextRegularAt
                      ? t('attendance.regularDoneBody', { time: formatDateTime(data.nextRegularAt, locale) })
                      : t('attendance.regularDoneBodyNoTime')}
                  </p>
                </div>
              </div>
            )}
            {data.stale && <p className="mt-2 max-w-xl text-body-sm text-warning-fg dark:text-warning-dark">{t('attendance.staleNote')}</p>}
            {blocked && <p className="mt-2 max-w-xl text-body-sm text-warning-fg dark:text-warning-dark">{t(agentBlockKey(data.agent.status))}</p>}
            {breakOver && overrunCharged(data, breakType) && (
              <p className="mt-2 max-w-xl text-body-sm text-error-fg dark:text-error-dark">
                {t('attendance.overLimitNow', { days: formatDays(breakType!.penaltyDays, locale) })}
              </p>
            )}
            {data.session && data.hours && <DayHoursNote hours={data.hours} />}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {startable && actions.canStartRegular && (
              <Button
                variant="primary"
                size="lg"
                icon={<LogIn className="h-4 w-4 flip-rtl" />}
                loading={attendance.busy}
                // The server refuses it anyway; the button says so first (the agent CR's §46.1).
                disabled={blocked}
                onClick={() => setAsking({ kind: 'start' })}
              >
                {t('attendance.startShift')}
              </Button>
            )}
            {startable && actions.canStartFree && (
              <Button
                variant="primary"
                size="lg"
                icon={<Sparkles className="h-4 w-4" />}
                loading={attendance.busy}
                disabled={blocked}
                onClick={() => setAsking({ kind: 'startFree' })}
              >
                {t('attendance.startFree')}
              </Button>
            )}
            {(onBreak || paused) && (
              <Button
                variant="primary"
                size="lg"
                icon={<Play className="h-4 w-4 flip-rtl" />}
                loading={attendance.busy}
                onClick={() => setAsking({ kind: paused ? 'unpause' : 'resume' })}
              >
                {t('attendance.resume')}
              </Button>
            )}
            {/* Kept for administrators and the employees picked in Settings › Attendance (2026-10-24). */}
            {working && !data.stale && data.pause?.allowed && (
              <Button size="lg" icon={<Pause className="h-4 w-4" />} disabled={attendance.busy} onClick={() => setAsking({ kind: 'pause' })}>
                {t('attendance.pauseWork')}
              </Button>
            )}
            {data.session && !isFreeShift(data.session) && (
              <Button
                size="lg"
                icon={<LogOut className="h-4 w-4 flip-rtl" />}
                disabled={attendance.busy}
                onClick={() => setAsking({ kind: 'end' })}
              >
                {t('attendance.endShift')}
              </Button>
            )}
            {data.session && isFreeShift(data.session) && (
              <Button
                size="lg"
                icon={<LogOut className="h-4 w-4 flip-rtl" />}
                disabled={attendance.busy}
                onClick={() => setAsking({ kind: 'endFree', sessionId: data.session!.id })}
              >
                {t('attendance.endFree')}
              </Button>
            )}
          </div>
        </div>

        {working && !data.stale && (
          <div className="mt-5 border-t hairline pt-4">
            <p className="mb-2 text-body-sm font-medium text-[var(--text-secondary)]">{t('attendance.takeBreak')}</p>
            {data.breakTypes.length === 0 ? (
              <p className="text-body-sm text-muted">{t('attendance.noBreakTypes')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.breakTypes.map((type) => (
                  <Button
                    key={type.id}
                    icon={type.needsApproval ? <ShieldCheck className="h-4 w-4" /> : <Coffee className="h-4 w-4" />}
                    // One request at a time: another waits until this one is decided or withdrawn. And
                    // none once its daily limit is used up (2026-10-28).
                    disabled={attendance.busy || (type.needsApproval && !!waiting) || usedUpToday(type)}
                    onClick={() => setAsking(type.needsApproval ? { kind: 'request', type } : { kind: 'break', type })}
                  >
                    {localized(type)}
                    {type.maxMinutes ? (
                      <span className="text-caption text-muted tabular">
                        {type.maxMinutes} {t('common.minutesShort')}
                      </span>
                    ) : null}
                    {type.leftToday != null && (
                      <span className="text-caption text-muted tabular">
                        ·{' '}
                        {usedUpToday(type) ? t('attendance.usedUpToday') : t('attendance.leftToday', { n: type.leftToday })}
                      </span>
                    )}
                    {type.needsApproval && <span className="text-caption text-muted">· {t('attendance.needsApproval')}</span>}
                  </Button>
                ))}
              </div>
            )}
            {waiting && <WaitingRequest request={waiting} busy={attendance.busy} onWithdraw={() => setAsking({ kind: 'withdraw', request: waiting })} />}
            {data.breakTypes.some((type) => type.needsApproval) && !waiting && (
              <p className="mt-3 max-w-2xl text-caption text-muted">{t('attendance.approvalNote')}</p>
            )}
            {data.breakTypes.some(overrunPenalised) && (
              <p className="mt-3 max-w-2xl text-caption text-muted">{t('attendance.overrunNote')}</p>
            )}
            {decided.length > 0 && <DecidedRequests requests={decided} />}
          </div>
        )}
      </Card>
      <ShiftConfirm action={asking} attendance={attendance} onClose={() => setAsking(null)} />

      <div className={clsx('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', pauses ? 'xl:grid-cols-6' : 'xl:grid-cols-5')}>
        <Metric label={t('attendance.worked')} value={formatMinutes(summary.workedMinutes, locale)} icon={<Clock className="h-4 w-4" />} />
        {/* Paused time is not worked time — and no break (2026-10-24). */}
        {pauses && (
          <Metric label={t('attendance.paused')} value={formatMinutes(summary.pausedMinutes ?? 0, locale)} icon={<Pause className="h-4 w-4" />} />
        )}
        <Metric
          label={t('attendance.breaks')}
          value={formatMinutes(summary.breakMinutes, locale)}
          hint={
            summary.workBreakMinutes
              ? t('attendance.workBreaks', { minutes: formatMinutes(summary.workBreakMinutes, locale) })
              : undefined
          }
          icon={<Coffee className="h-4 w-4" />}
        />
        <Metric
          label={t('attendance.notWorking')}
          value={formatMinutes(summary.notWorkingMinutes ?? 0, locale)}
          icon={<UserX className="h-4 w-4" />}
          tone={(summary.notWorkingMinutes ?? 0) > 0 ? 'error' : 'neutral'}
        />
        <Metric
          label={t('attendance.overrun')}
          value={formatMinutes(summary.overrunMinutes, locale)}
          tone={summary.overrunMinutes > 0 ? 'warning' : 'neutral'}
        />
        {/* Information only: lateness costs nothing — the day is judged on the hours worked (2026-10-21). */}
        <Metric label={t('attendance.late')} value={summary.lateMinutes == null ? '—' : formatMinutes(summary.lateMinutes, locale)} />
      </div>

      {/* The work date's Free Shifts (CR-006), each its own record — apart from the day's hours above. */}
      {data.free && (data.free.count > 0 || actions.canStartFree) && (
        <FreeShiftsCard free={data.free} tracked={data.tracked ?? null} />
      )}

      {/* `?computer=`: a notification about one of my computers opens here with it marked. */}
      <AgentPanel agent={data.agent} focus={params.get('computer')} />

      <Card padded={false}>
        <CardHeader title={t('attendance.history')} className="p-5 pb-0" />
        {history.isLoading ? (
          <LoadingBlock />
        ) : history.error ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} />
        ) : !history.data?.items.length ? (
          <EmptyState title={t('attendance.noShiftsYet')} hint="" />
        ) : (
          <ShiftsTable items={history.data.items} />
        )}
      </Card>
    </div>
  );
}

/**
 * The work date's Free Shifts (CR-006): tracked, unpaid, never in its hours — a row each, numbered after
 * the Regular Shift they follow; an open one's figures so far. Then the three figures kept apart.
 */
function FreeShiftsCard({ free, tracked }: { free: FreeShifts; tracked: { regularMinutes: number; freeMinutes: number; totalMinutes: number } | null }) {
  const { t, locale } = useI18n();
  const span = (minutes: number) => formatMinutes(minutes, locale);
  return (
    <Card padded={false}>
      <CardHeader title={t('attendance.freeShifts')} subtitle={t('attendance.freeUnpaid')} className="p-5 pb-0" />
      {free.sessions.length === 0 ? (
        <p className="px-5 pb-5 pt-3 text-body-sm text-muted">{t('attendance.freeShiftsAfterHint')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('attendance.freeShift')}</Th>
              <Th>{t('attendance.started')}</Th>
              <Th>{t('attendance.ended')}</Th>
              <Th align="end">{t('attendance.notWorking')}</Th>
              <Th align="end">{t('attendance.worked')}</Th>
            </tr>
          </thead>
          <tbody>
            {free.sessions.map((s) => (
              <Tr key={s.id}>
                <Td>
                  <Link to={`/attendance/sessions/${s.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {t('attendance.freeShiftNumber', { n: s.number })}
                  </Link>
                </Td>
                <Td className="tabular text-muted">{formatShortTime(s.startedAt, locale)}</Td>
                <Td className="tabular text-muted">
                  {s.endedAt ? formatShortTime(s.endedAt, locale) : <Badge tone="success" dot>{t('attendance.stillOpen')}</Badge>}
                  <SessionFlags session={s} />
                </Td>
                <Td align="end" className="tabular">
                  <NotWorkingCell minutes={s.summary.notWorkingMinutes} idle={undefined} />
                </Td>
                <Td align="end" className="tabular">
                  {span(s.summary.workedMinutes)}
                  {!s.endedAt && <span className="block text-caption text-faint">{t('attendance.freeSoFar')}</span>}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {tracked && (
        <p className="border-t hairline px-5 py-3 text-caption text-muted tabular">
          {t('attendance.regularShift')} {span(tracked.regularMinutes)} · {t('attendance.freeShifts')} {span(tracked.freeMinutes)} ·{' '}
          <span className="font-medium text-[var(--text-secondary)]">
            {t('attendance.trackedTotal')} {span(tracked.totalMinutes)}
          </span>{' '}
          <span className="text-faint">({t('attendance.trackedTotalHint')})</span>
        </p>
      )}
    </Card>
  );
}

/** A shift as a list of them reads it — a Free Shift with its number after the Regular Shift it follows (CR-006). */
export type HistoryShift = AttendanceSession & { summary: DaySummary; number?: number | null };

/** `GET /attendance/history`: someone's shifts from–to, newest first, and whose they are. */
export interface ShiftHistory {
  from: string;
  to: string;
  user: {
    id: string;
    fullName: string;
    fullNameAr: string | null;
    employeeCode: string | null;
    department: { id: string; name: string; nameAr: string | null; code: string } | null;
  };
  items: HistoryShift[];
  /** The period's Regular, Free and Total tracked figures, kept apart (CR-006). Missing from an older API. */
  totals?: {
    regular: { count: number; workedMinutes: number };
    free: { count: number; workedMinutes: number };
    regularMinutes: number;
    freeMinutes: number;
    totalMinutes: number;
  };
}

/**
 * Shifts, a row each: the day — to the shift's page — its start and end, breaks,
 * Not working and Worked. My shift's Recent shifts, and an employee's month of
 * shifts (2026-10-27), which passes `back` so the shift's page returns to it.
 */
export function ShiftsTable({ items, back }: { items: HistoryShift[]; back?: string }) {
  const { t, locale } = useI18n();
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('attendance.day')}</Th>
          <Th>{t('attendance.started')}</Th>
          <Th>{t('attendance.ended')}</Th>
          <Th>{t('attendance.breaks')}</Th>
          <Th align="end">{t('attendance.notWorking')}</Th>
          <Th align="end">{t('attendance.worked')}</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((s) => (
          <Tr key={s.id}>
            <Td className="tabular">
              <Link
                to={`/attendance/sessions/${s.id}`}
                state={back ? { back } : undefined}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                {formatDate(s.workDate, locale)}
              </Link>
              {/* A Free Shift: tracked, unpaid — never the day's hours (CR-006). */}
              {isFreeShift(s) && (
                <span className="mt-0.5 block">
                  <Badge tone="info">{t('attendance.freeShiftNumber', { n: s.number ?? 1 })}</Badge>
                </span>
              )}
            </Td>
            <Td className="tabular text-muted">{formatShortTime(s.startedAt, locale)}</Td>
            <Td className="tabular text-muted">
              {s.endedAt ? formatShortTime(s.endedAt, locale) : <Badge tone="success" dot>{t('attendance.stillOpen')}</Badge>}
              <SessionFlags session={s} />
            </Td>
            <Td>
              <BreakList breaks={s.breaks} />
            </Td>
            <Td align="end" className="tabular">
              <NotWorkingCell minutes={s.summary.notWorkingMinutes} idle={s.idle} />
            </Td>
            <Td align="end" className="tabular">{formatMinutes(s.summary.workedMinutes, locale)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

/**
 * A break asked for and not yet decided: the employee is still on shift and
 * working — the agent sees them that way — until an administrator approves it.
 */
function WaitingRequest({ request, busy, onWithdraw }: { request: BreakRequestRef; busy: boolean; onWithdraw: () => void }) {
  const { t, locale, localized } = useI18n();
  return (
    <div
      role="status"
      className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-warning/35 bg-warning/8 px-3.5 py-3"
    >
      <Hourglass className="h-4 w-4 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
      <div className="min-w-0 grow basis-48">
        <p className="text-body-sm font-medium text-[var(--text-primary)]">
          {t('attendance.requestWaiting', { name: localized(request.breakType) })}
        </p>
        <p className="text-caption text-muted">
          {t('attendance.askedAt', { time: formatShortTime(request.requestedAt, locale) })} · {t('attendance.requestWaitingBody')}
        </p>
      </div>
      <Button size="sm" icon={<Undo2 className="h-3.5 w-3.5 flip-rtl" />} disabled={busy} onClick={onWithdraw}>
        {t('attendance.withdrawRequest')}
      </Button>
    </div>
  );
}

/** What came of this shift's earlier requests: approved, rejected with its reason, or withdrawn. */
function DecidedRequests({ requests }: { requests: BreakRequestRef[] }) {
  const { t, locale, localized } = useI18n();
  return (
    <div className="mt-4">
      <p className="mb-1.5 text-caption font-medium text-[var(--text-secondary)]">{t('attendance.breakRequests')}</p>
      <ul className="space-y-1 text-caption">
        {requests.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-[var(--text-primary)]">{localized(r.breakType)}</span>
            <Badge tone={BREAK_REQUEST_TONE[r.status]}>{enumLabel(r.status, locale)}</Badge>
            <span className="text-muted">
              {r.status === 'CANCELLED' && r.cancelReason
                ? t(`breakRequest.cancel.${r.cancelReason}` as TranslationKey)
                : r.decidedBy
                  ? t('breakRequest.decidedBy', { name: localized(r.decidedBy) })
                  : null}
              {r.decidedAt && <span className="tabular"> · {formatShortTime(r.decidedAt, locale)}</span>}
            </span>
            {/* The approver's own words, in their own direction: an English reason on an Arabic page keeps its quotes. */}
            {r.status === 'REJECTED' && r.decisionNote && (
              <span dir="auto" className="basis-full text-muted">
                “{r.decisionNote}”
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The day's hours, for whoever may pause (operator, 2026-10-24: "he must complete his
 * working hours before ending the shift"): worked so far, still to work, and what a day
 * that ends short costs.
 */
function DayHoursNote({ hours }: { hours: DayHours }) {
  const { t, locale } = useI18n();
  const span = (minutes: number) => formatMinutes(minutes, locale);
  return (
    <div className="mt-3 max-w-xl space-y-1">
      <p className={clsx('text-body-sm font-medium tabular', hours.met ? 'text-success-fg dark:text-success' : 'text-[var(--text-primary)]')}>
        {hours.met
          ? t('attendance.hoursDone', { owed: span(hours.owedMinutes) })
          : t('attendance.hoursLeft', { worked: span(hours.workedMinutes), owed: span(hours.owedMinutes), missing: span(hours.missingMinutes) })}
      </p>
      {!hours.met && Number(hours.shortDayPenaltyDays) > 0 && (
        <p className="text-body-sm text-muted">{t('attendance.pauseOwesHours', { days: formatDays(hours.shortDayPenaltyDays, locale) })}</p>
      )}
    </div>
  );
}

/** Not working on a shift: the minutes, and the inactivity penalties under them. */
function NotWorkingCell({ minutes, idle }: { minutes: number | undefined; idle: AttendanceSession['idle'] }) {
  const { t, locale } = useI18n();
  if (!minutes && !idle?.penalised) return <span className="text-faint">—</span>;
  return (
    <>
      <span className={minutes ? 'text-error-fg dark:text-error-dark' : 'text-muted'}>{formatMinutes(minutes ?? 0, locale)}</span>
      {idle && idle.penalised > 0 && (
        <span className="block text-caption text-error-fg dark:text-error-dark">{t('attendance.penalty', { days: formatDays(idle.penaltyDays, locale) })}</span>
      )}
    </>
  );
}

function SessionFlags({ session }: { session: Pick<AttendanceSession, 'autoClosed' | 'endUnconfirmed' | 'closedBy' | 'closeNote'> }) {
  const { t, localized } = useI18n();
  if (session.endUnconfirmed) {
    return (
      <span className="mt-0.5 block text-caption text-warning-fg dark:text-warning-dark" title={session.closeNote ?? undefined}>
        {t('attendance.endUnconfirmed')}
      </span>
    );
  }
  if (!session.autoClosed && !session.closedBy) return null;
  return (
    <span className="mt-0.5 block text-caption text-warning-fg dark:text-warning-dark" title={session.closeNote ?? undefined}>
      {session.autoClosed ? t('attendance.autoClosed') : t('attendance.closedBy', { name: localized(session.closedBy) })}
    </span>
  );
}

function BreakList({ breaks }: { breaks: AttendanceSession['breaks'] }) {
  const { t, locale, localized } = useI18n();
  if (!breaks.length) return <span className="text-faint">—</span>;
  return (
    <ul className="space-y-0.5 text-caption">
      {breaks.map((b) => {
        const minutes = b.endedAt ? Math.floor((new Date(b.endedAt).getTime() - new Date(b.startedAt).getTime()) / 60_000) : null;
        const over = minutes != null && b.breakType.maxMinutes != null && minutes > b.breakType.maxMinutes;
        return (
          <li key={b.id} className={over ? 'text-error-fg dark:text-error-dark' : 'text-muted'}>
            {/* A pause is no break: it says Paused, whatever its type is called. */}
            {isPause(b) ? t('attendance.paused') : localized(b.breakType)} · <span className="tabular">{formatShortTime(b.startedAt, locale)}</span>
            {minutes != null ? (
              <span className="tabular"> · {formatMinutes(minutes, locale)}</span>
            ) : (
              <span> · {t('attendance.stillOpen')}</span>
            )}
            {b.breakType.countsAsWork && <span> · {t('attendance.countsAsWork')}</span>}
            {b.penalty && (
              <span className="whitespace-nowrap">
                {' · '}
                {t('attendance.penalty', { days: formatDays(b.penalty.days, locale) })} ({enumLabel(b.penalty.status, locale)})
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Of the breaks, the time on types that count as work (a meeting) — it stayed in Worked. */
function WorkBreaksNote({ minutes }: { minutes: number | undefined }) {
  const { t, locale } = useI18n();
  if (!minutes) return null;
  return (
    <span className="block text-caption text-muted">{t('attendance.workBreaks', { minutes: formatMinutes(minutes, locale) })}</span>
  );
}

/* -------------------------------------------------------------------------- */
/* The team board                                                              */
/* -------------------------------------------------------------------------- */

/** The hours owed the day — their shift's Standard hours — against Worked, or what it came to once judged (`GET /attendance/board`). */
interface DayTargetView {
  requiredMinutes: number;
  state: 'MET' | 'IN_PROGRESS' | 'SHORT';
  missingMinutes: number;
  extraMinutes: number;
  /** SHIFT: its Standard hours. DEFAULT: no shift with any, so the company's default. SALARY_RECORD: a day judged before 21 Oct 2026. */
  source: 'SHIFT' | 'DEFAULT' | 'SALARY_RECORD';
  /** The day is over and judged: this is what was decided. */
  decided: boolean;
}

const TARGET_SOURCE: Record<DayTargetView['source'], TranslationKey> = {
  SHIFT: 'attendance.targetFromShift',
  DEFAULT: 'attendance.targetFromDefault',
  SALARY_RECORD: 'attendance.targetFromSalary',
};

/** Met (and by how much past), still to go while the day runs, or short once they stopped. */
function TargetCell({ target }: { target: DayTargetView | null | undefined }) {
  const { t, locale } = useI18n();
  if (!target) return <span className="text-faint">—</span>;
  const span = (minutes: number) => formatMinutes(minutes, locale);
  const source = t(TARGET_SOURCE[target.source] ?? 'attendance.targetFromDefault');
  return (
    <>
      <span
        className={clsx('whitespace-nowrap', target.source === 'DEFAULT' && 'text-muted')}
        title={target.decided ? `${source} · ${t('attendance.targetJudged')}` : source}
      >
        {span(target.requiredMinutes)}
      </span>
      {target.state === 'MET' ? (
        <span className="block whitespace-nowrap text-caption text-success-fg dark:text-success">
          {target.extraMinutes > 0
            ? t('attendance.targetMetExtra', { minutes: span(target.extraMinutes) })
            : t('attendance.targetMet')}
        </span>
      ) : target.state === 'SHORT' ? (
        <span className="block whitespace-nowrap text-caption text-error-fg dark:text-error-dark">
          {t('attendance.targetShort', { minutes: span(target.missingMinutes) })}
        </span>
      ) : (
        <span className="block whitespace-nowrap text-caption text-muted">
          {t('attendance.targetToGo', { minutes: span(target.missingMinutes) })}
        </span>
      )}
    </>
  );
}

const STATUS_TONE: Record<string, BadgeTone> = {
  WORKING: 'success',
  ON_BREAK: 'warning',
  PAUSED: 'info',
  ENDED: 'neutral',
  ON_LEAVE: 'info',
  OFF: 'neutral',
  UPCOMING: 'neutral',
  NOT_STARTED: 'warning',
  ABSENT: 'error',
  NO_SCHEDULE: 'neutral',
};

const AGENT_DOT: Record<string, 'online' | 'offline' | 'alert' | 'neutral'> = {
  CONNECTED: 'online',
  OFFLINE: 'offline',
  DISABLED: 'alert',
  NOT_PAIRED: 'neutral',
};

function TeamBoard() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? '';
  const departmentId = params.get('departmentId') ?? '';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const { data: departments } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => api.get<Paginated<any>>('/departments', { pageSize: 100 }),
  });

  const board = useQuery({
    queryKey: ['attendance', 'board', date, departmentId],
    queryFn: () => api.get<any>('/attendance/board', { date: date || undefined, departmentId: departmentId || undefined }),
    // Presence moves on its own; the board keeps up with it.
    refetchInterval: 30_000,
  });

  const data = board.data;
  const order = ['WORKING', 'ON_BREAK', 'PAUSED', 'NOT_STARTED', 'ABSENT', 'ENDED', 'ON_LEAVE', 'UPCOMING', 'OFF', 'NO_SCHEDULE'];

  return (
    <div className="space-y-4">
      <Card padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Input
            type="date"
            aria-label={t('attendance.day')}
            value={date || data?.today || isoDate()}
            onChange={(e) => setFilter('date', e.target.value === data?.today ? '' : e.target.value)}
          />
          <Select value={departmentId} onChange={(e) => setFilter('departmentId', e.target.value)} aria-label={t('common.department')}>
            <option value="">{t(can(P.USERS_VIEW_ALL) ? 'attendance.allDepartments' : 'reach.allMyDepartments')}</option>
            {departments?.items.map((d: any) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>
        </div>
        {data && (
          <div className="flex flex-wrap items-center gap-2 border-t hairline px-4 py-2.5">
            {data.holiday && <Badge tone="plasma">{t('attendance.holiday', { name: localized(data.holiday) })}</Badge>}
            {order
              .filter((status) => data.counts[status])
              .map((status) => (
                <Badge key={status} tone={STATUS_TONE[status]} className="tabular">
                  {t(`attendance.status.${status}` as TranslationKey)} · {data.counts[status]}
                </Badge>
              ))}
            {data.notWorking > 0 && (
              <Badge tone="error" className="tabular">
                {t('presence.NOT_WORKING')} · {data.notWorking}
              </Badge>
            )}
            {data.freeShiftNow > 0 && (
              <Badge tone="info" className="tabular">
                {t('attendance.freeShiftNow', { n: data.freeShiftNow })}
              </Badge>
            )}
            {data.agentRequired && <Badge tone="brand">{t('agent.requiredBadge')}</Badge>}
          </div>
        )}
      </Card>

      <Card padded={false}>
        {board.isLoading ? (
          <LoadingBlock />
        ) : board.error ? (
          <ErrorState error={board.error} onRetry={() => void board.refetch()} />
        ) : !data?.rows.length ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th>{t('attendance.roster')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('attendance.started')}</Th>
                <Th>{t('attendance.ended')}</Th>
                <Th>{t('attendance.breaks')}</Th>
                <Th align="end">{t('attendance.worked')}</Th>
                <Th align="end">{t('attendance.dailyHours')}</Th>
                <Th align="end">{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {[...data.rows]
                .sort((a: any, b: any) => order.indexOf(a.status) - order.indexOf(b.status))
                .map((row: any) => {
                  const latest = row.sessions[row.sessions.length - 1];
                  return (
                    <Tr key={row.user.id}>
                      <Td>
                        <span className="flex items-center gap-2">
                          <span
                            title={`${t('agent.title')}: ${t(`agent.status.${row.agent?.status ?? 'NOT_PAIRED'}` as TranslationKey)}${
                              row.agent?.deviceName ? ` · ${row.agent.deviceName}` : ''
                            }`}
                          >
                            <StatusDot tone={AGENT_DOT[row.agent?.status ?? 'NOT_PAIRED']} />
                            <span className="sr-only">{t(`agent.status.${row.agent?.status ?? 'NOT_PAIRED'}` as TranslationKey)}</span>
                          </span>
                          {/* Their month of shifts — the board's day's month — and back to the board as it is (2026-10-27). */}
                          <Link
                            to={`/attendance/employees/${row.user.id}${date ? `?month=${date.slice(0, 7)}` : ''}`}
                            state={{ back: `/attendance?${params.toString()}` }}
                            className="block hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {localized(row.user)}
                          </Link>
                        </span>
                        {row.department && <span className="ms-4 text-caption text-faint">{localized(row.department)}</span>}
                      </Td>
                      <Td className="text-muted">
                        {row.roster?.startAt ? (
                          <>
                            <span className="block">{localized({ name: row.roster.shiftName ?? '', nameAr: row.roster.shiftNameAr })}</span>
                            <TimeRange from={row.roster.startAt} to={row.roster.endAt} className="tabular text-caption" />
                          </>
                        ) : row.roster?.type === 'OFF' ? (
                          t('attendance.status.OFF')
                        ) : (
                          <span className="text-faint">—</span>
                        )}
                      </Td>
                      <Td>
                        <Badge
                          tone={STATUS_TONE[row.status] ?? 'neutral'}
                          dot={row.status === 'WORKING' || row.status === 'ON_BREAK' || row.status === 'PAUSED'}
                        >
                          {t(`attendance.status.${row.status}` as TranslationKey)}
                        </Badge>
                        {row.status === 'WORKING' && row.presence?.status === 'NOT_WORKING' && (
                          <span className="mt-0.5 block">
                            <PresenceBadge presence={row.presence} compact />
                            <span className="ms-1 text-caption text-muted">
                              {t('attendance.since', { time: formatShortTime(row.presence.since, locale) })}
                            </span>
                          </span>
                        )}
                        {row.openBreak && (
                          <span className="mt-0.5 block text-caption text-muted">
                            {/* The badge already says Paused: the pause needs only its start. */}
                            {isPause(row.openBreak)
                              ? t('attendance.since', { time: formatShortTime(row.openBreak.startedAt, locale) })
                              : `${localized(row.openBreak.breakType)} · ${t('attendance.since', { time: formatShortTime(row.openBreak.startedAt, locale) })}`}
                          </span>
                        )}
                        {/* Asked for a break that waits for an administrator's approval. */}
                        {row.breakRequest && (
                          <span className="mt-0.5 block text-caption text-warning-fg dark:text-warning-dark">
                            {t('attendance.requestWaiting', { name: localized(row.breakRequest.breakType) })} ·{' '}
                            {t('attendance.askedAt', { time: formatShortTime(row.breakRequest.requestedAt, locale) })}
                          </span>
                        )}
                        {row.leave && <span className="mt-0.5 block text-caption text-muted">{localized(row.leave.leaveType)}</span>}
                        {/* After the day's Regular Shift: tracked, unpaid (CR-006). */}
                        {row.free?.open && (
                          <span className="mt-0.5 block">
                            <Badge tone="info" dot>
                              {t('attendance.inFreeShift')}
                            </Badge>
                          </span>
                        )}
                      </Td>
                      <Td className="tabular text-muted">
                        {row.summary.firstStartAt ? formatShortTime(row.summary.firstStartAt, locale) : '—'}
                        {/* Information only — the Daily hours column says whether the day was worked (2026-10-21). */}
                        {(row.summary.lateMinutes ?? 0) > 0 && (
                          <span className="block text-caption text-faint">
                            {t('attendance.late')} {formatMinutes(row.summary.lateMinutes, locale)}
                          </span>
                        )}
                      </Td>
                      <Td className="tabular text-muted">
                        {row.summary.lastEndAt ? formatShortTime(row.summary.lastEndAt, locale) : '—'}
                        {(row.summary.leftEarlyMinutes ?? 0) > 0 && (
                          <span className="block text-caption text-faint">
                            {t('attendance.leftEarly')} {formatMinutes(row.summary.leftEarlyMinutes, locale)}
                          </span>
                        )}
                        {row.sessions.map((s: any) => (
                          <SessionFlags key={s.id} session={s} />
                        ))}
                      </Td>
                      <Td className="tabular text-muted">
                        {row.summary.breakMinutes > 0 ? formatMinutes(row.summary.breakMinutes, locale) : '—'}
                        {row.summary.overrunMinutes > 0 && (
                          <span className="block text-caption text-error-fg dark:text-error-dark">+{formatMinutes(row.summary.overrunMinutes, locale)}</span>
                        )}
                        <WorkBreaksNote minutes={row.summary.workBreakMinutes} />
                        {/* Paused: off worked time, and no break — said apart (2026-10-24). */}
                        {(row.summary.pausedMinutes ?? 0) > 0 && (
                          <span className="block whitespace-nowrap text-caption text-muted">
                            {t('attendance.pausedFor', { minutes: formatMinutes(row.summary.pausedMinutes, locale) })}
                          </span>
                        )}
                      </Td>
                      <Td align="end" className="tabular">
                        <span className="whitespace-nowrap">
                          {row.sessions.length ? formatMinutes(row.summary.workedMinutes, locale) : '—'}
                        </span>
                        {row.summary.notWorkingMinutes > 0 && (
                          <span className="block text-caption text-error-fg dark:text-error-dark">
                            {t('attendance.notWorkingShort', { minutes: formatMinutes(row.summary.notWorkingMinutes, locale) })}
                          </span>
                        )}
                        {row.idle?.penalised > 0 && (
                          <span className="block text-caption text-error-fg dark:text-error-dark">
                            {t('attendance.penalty', { days: formatDays(row.idle.penaltyDays, locale) })}
                          </span>
                        )}
                        {/* Free Shifts beside the day's hours, never in them (CR-006). */}
                        {row.free?.count > 0 && (
                          <span
                            className="block whitespace-nowrap text-caption text-muted"
                            title={`${t('attendance.trackedTotal')}: ${formatMinutes(row.tracked?.totalMinutes ?? 0, locale)}`}
                          >
                            {t('attendance.freeMinutesShort', { minutes: formatMinutes(row.free.workedMinutes, locale) })}
                          </span>
                        )}
                      </Td>
                      <Td align="end" className="tabular">
                        <TargetCell target={row.target} />
                      </Td>
                      <Td align="end">
                        <span className="inline-flex flex-wrap justify-end gap-1">
                          {latest && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/attendance/sessions/${latest.id}`)}>
                              {t('attendance.details')}
                            </Button>
                          )}
                          {row.openSessionId && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/attendance/sessions/${row.openSessionId}/end`)}>
                              {row.openSessionKind === 'FREE' ? t('attendance.endFree') : t('attendance.endShift')}
                            </Button>
                          )}
                          {/* A forgotten shift no agent tracked: its day waits for a manager to set the real end. */}
                          {row.sessions
                            .filter((s: any) => s.endUnconfirmed)
                            .map((s: any) => (
                              <Button key={s.id} size="sm" variant="ghost" onClick={() => navigate(`/attendance/sessions/${s.id}/end`)}>
                                {t('attendance.setEnd')}
                              </Button>
                            ))}
                        </span>
                      </Td>
                    </Tr>
                  );
                })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The team's computers                                                        */
/* -------------------------------------------------------------------------- */

interface TeamComputer {
  id: string;
  name: string;
  osName: string;
  osVersion: string | null;
  agentVersion: string | null;
  status: 'ACTIVE' | 'DISABLED' | 'REVOKED';
  online: boolean;
  asleep: boolean;
  pairedAt: string;
  lastHeartbeatAt: string | null;
  statusReason: string | null;
  statusChangedAt: string | null;
  statusChangedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  user: { id: string; fullName: string; fullNameAr: string | null; employeeCode: string | null };
}

/**
 * The computers the team runs the Desktop Agent on (the agent CR's §47): whose,
 * which, connected or not, when each last reported — never anyone's activity.
 * An administrator disables, enables or removes one (`attendance.devices.manage`).
 */
function ComputersBoard() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const manage = can(P.ATTENDANCE_DEVICES_MANAGE);

  const computers = useQuery({
    queryKey: ['attendance', 'devices', status],
    queryFn: () =>
      api.get<{ items: TeamComputer[]; counts: { total: number; online: number; disabled: number } }>('/attendance/devices', {
        status: status || undefined,
      }),
    refetchInterval: 30_000,
  });

  const enable = useMutation({
    mutationFn: (id: string) => api.post(`/attendance/devices/${id}/enable`, {}),
    onSuccess: () => {
      toast.success(t('devices.enabled'));
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'devices'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = computers.data;
  return (
    <div className="space-y-4">
      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <Select
            value={status}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('status', e.target.value);
              else next.delete('status');
              setParams(next, { replace: true });
            }}
            aria-label={t('common.status')}
            className="max-w-[14rem]"
          >
            <option value="">{t('devices.inUseOrDisabled')}</option>
            <option value="ACTIVE">{t('devices.status.ACTIVE')}</option>
            <option value="DISABLED">{t('devices.status.DISABLED')}</option>
            <option value="REVOKED">{t('devices.status.REVOKED')}</option>
          </Select>
          {data && (
            <span className="text-body-sm text-muted tabular">
              {t('devices.counts', { online: String(data.counts.online), total: String(data.counts.total) })}
            </span>
          )}
        </div>
        <p className="border-t hairline px-4 py-2.5 text-caption text-muted">{t('devices.noTelemetry')}</p>
      </Card>

      <Card padded={false}>
        {computers.isLoading ? (
          <LoadingBlock />
        ) : computers.error ? (
          <ErrorState error={computers.error} onRetry={() => void computers.refetch()} />
        ) : !data?.items.length ? (
          <EmptyState title={t('devices.none')} hint={t('devices.noneHint')} icon={<MonitorOff className="h-5 w-5" />} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th>{t('devices.computer')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('devices.lastReportCol')}</Th>
                {manage && <Th align="end">{t('common.actions')}</Th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    <span className="block">{localized(c.user)}</span>
                    {c.user.employeeCode && <span className="text-caption text-faint" dir="ltr">{c.user.employeeCode}</span>}
                  </Td>
                  <Td>
                    <span className="block">{c.name}</span>
                    <span className="text-caption text-muted">
                      {[c.osName, c.osVersion, c.agentVersion && t('agent.version', { version: c.agentVersion })].filter(Boolean).join(' · ')}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge tone={c.status === 'ACTIVE' ? 'success' : c.status === 'DISABLED' ? 'error' : 'neutral'}>
                        {t(`devices.status.${c.status}` as TranslationKey)}
                      </Badge>
                      {c.status === 'ACTIVE' && (
                        <span className="inline-flex items-center gap-1.5 text-caption text-muted">
                          <StatusDot tone={c.online ? 'online' : 'offline'} />
                          {c.online ? t('devices.online') : c.asleep ? t('devices.asleep') : t('devices.offline')}
                        </span>
                      )}
                    </span>
                    {c.statusReason && c.status !== 'ACTIVE' && (
                      <span className="mt-0.5 block text-caption text-muted" title={c.statusChangedBy ? localized(c.statusChangedBy) : undefined}>
                        {c.statusReason}
                      </span>
                    )}
                  </Td>
                  <Td className="text-muted">
                    {c.lastHeartbeatAt ? relativeTime(c.lastHeartbeatAt, locale) : t('agent.never')}
                    <span className="block text-caption text-faint">{t('devices.connectedSince', { date: formatDate(c.pairedAt, locale) })}</span>
                  </Td>
                  {manage && (
                    <Td align="end">
                      <span className="inline-flex flex-wrap justify-end gap-1">
                        {c.status === 'ACTIVE' && (
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/attendance/devices/${c.id}/disable`)}>
                            {t('devices.disable')}
                          </Button>
                        )}
                        {c.status === 'DISABLED' && (
                          <Button size="sm" variant="ghost" loading={enable.isPending && enable.variables === c.id} onClick={() => enable.mutate(c.id)}>
                            {t('devices.enable')}
                          </Button>
                        )}
                        {c.status !== 'REVOKED' && (
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/attendance/devices/${c.id}/remove`)}>
                            {t('devices.remove')}
                          </Button>
                        )}
                      </span>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

